import { ChangeDetectionStrategy, Component, EventEmitter, input, Output, TemplateRef } from '@angular/core';
import { NestedValuePipe } from '../../../utils';
import { NestedListData } from '../../models/nested-list-data.model';
import { NestedSuggesterParent } from '../../models/nested-suggester-parent.interface';

@Component({
  standalone: false,
  selector: 'f-multiple-suggester-nested-item',
  templateUrl: './multiple-suggester-nested-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyMultipleSuggesterNestedItemComponent {
  item = input.required<NestedListData>();
  level = input<number>(0);
  maxLevel = input<number>(4);
  childrenPath = input<string>('children');
  idField = input<string>('id');
  valuePath = input<string>('name');
  linkPath = input<string>('link');
  itemCssClass = input<string>('');
  disabledPath = input<string>('');
  expandedItems = input.required<Set<string>>();
  suggesterItemTemplate = input<TemplateRef<unknown>>();
  inputValue = input<string>('');
  nestedValuePipe = input.required<NestedValuePipe>();
  parentComponent = input<NestedSuggesterParent | null>(null);

  @Output() toggleExpand = new EventEmitter<NestedListData>();
  @Output() checkboxChange = new EventEmitter<{ item: NestedListData; event: Event }>();
  @Output() itemClick = new EventEmitter<NestedListData>();
  @Output() enterKey = new EventEmitter<{ event: Event; item: NestedListData }>();

  private levelIndentation: number = 8;

  get hasChildren(): boolean {
    if (this.level() >= this.maxLevel()) return false;
    const parent = this.parentComponent();
    if (parent?.getChildren) {
      const children = parent.getChildren(this.item());
      return Array.isArray(children) && children.length > 0;
    }
    const children = this.nestedValuePipe().transform(this.item(), this.childrenPath());
    return Array.isArray(children) && children.length > 0;
  }

  get isExpanded(): boolean {
    return this.expandedItems().has(this.item()[this.idField()] as string);
  }

  get children(): NestedListData[] {
    const parent = this.parentComponent();
    if (parent?.getChildren) {
      return parent.getChildren(this.item());
    }
    return this.nestedValuePipe().transform(this.item(), this.childrenPath()) || [];
  }

  get isDisabled(): boolean {
    if (this.disabledPath() && (this.nestedValuePipe().transform(this.item(), this.disabledPath()) as boolean)) {
      return true;
    }
    return this.isDisabledByLevelLimit;
  }

  get isDisabledByLevelLimit(): boolean {
    const parent = this.parentComponent();
    return parent?.isDisabledByLevelLimit ? parent.isDisabledByLevelLimit(this.item()) : false;
  }

  get disabledTooltip(): string {
    const parent = this.parentComponent();
    return this.isDisabledByLevelLimit && parent?.getLevelLimitMessage ? parent.getLevelLimitMessage() : '';
  }

  get isChecked(): boolean {
    const parent = this.parentComponent();
    if (parent?.areAllChildrenSelected && parent.hasChildren(this.item())) {
      return parent.areAllChildrenSelected(this.item());
    }
    return this.item().isSelected || false;
  }

  get isIndeterminate(): boolean {
    const parent = this.parentComponent();
    return parent?.isIndeterminate ? parent.isIndeterminate(this.item()) : false;
  }

  onToggleExpand(event: Event) {
    event.stopPropagation();
    this.toggleExpand.emit(this.item());
  }

  onCheckboxChange(event: Event) {
    this.checkboxChange.emit({ item: this.item(), event });
    this.itemClick.emit(this.item());
  }

  onEnterKey(event: Event) {
    this.enterKey.emit({ event, item: this.item() });
  }

  indentationStyle(): { [key: string]: string } {
    const level = this.level();
    const indent = level === 0 ? this.levelIndentation : this.levelIndentation + level * 24;
    return { 'padding-left': `${indent}px` };
  }
}
